import { useCallback, useRef, useState } from "react";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import { MarqueeRow, StageFX } from "./CasinoFX";
import "./arcade-anim.css";

const Boxer = ({ x, color, flip, figCls }) => (
  <g transform={`translate(${x},118) ${flip ? "scale(-1,1)" : ""}`}>
    <g className={`arc-fig ${figCls}`}>
      <line x1="-6" y1="-14" x2="-10" y2="4" stroke="#1b2130" strokeWidth="5" strokeLinecap="round" />
      <line x1="6" y1="-14" x2="10" y2="4" stroke="#1b2130" strokeWidth="5" strokeLinecap="round" />
      <rect x="-11" y="-46" width="22" height="34" rx="8" fill={color} />
      <rect x="-11" y="-19" width="22" height="10" rx="4" fill="#0B0E14" />
      <circle cx="0" cy="-56" r="10" fill="#E8B88A" />
      <circle cx="4.5" cy="-58" r="1.6" fill="#0B0E14" />
      <path d="M -9 -61 A 10 10 0 0 1 9 -61" stroke={color} strokeWidth="4.5" fill="none" />
      <line x1="-5" y1="-40" x2="-14" y2="-30" stroke={color} strokeWidth="5" strokeLinecap="round" />
      <circle cx="-15" cy="-28" r="7" fill="#F23645" />
      <line x1="6" y1="-40" x2="22" y2="-35" stroke={color} strokeWidth="5" strokeLinecap="round" />
      <circle cx="26" cy="-34" r="8" fill="#F23645" />
    </g>
  </g>
);

/* 🥊 Boxing KO — animated ring: your blue boxer vs the red champ. One real
   1-tick Rise/Fall contract per punch (~×1.9). Rise/Fall scanner picks the market. */
const BoxingKO = ({ runContract, busy }) => {
  const [state, setState] = useState("idle"); // idle | punching | win | lose
  const [res, setRes] = useState(null);
  const [tally, setTally] = useState({ ko: 0, miss: 0, net: 0 });
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);
  const auto = useAutoRun("boxing");

  const punch = async (dir) => {
    if (state === "punching") return null;
    setState("punching"); setRes(null);
    const r = await runContract({
      contractType: dir === "up" ? "CALL" : "PUT",
      side: dir === "up" ? "RISE" : "FALL", label: "Boxing KO", symbol: symRef.current,
    });
    if (!r) { setState("idle"); return null; }
    setRes(r);
    setState(r.won ? "win" : "lose");
    setTally((t) => ({ ko: t.ko + (r.won ? 1 : 0), miss: t.miss + (r.won ? 0 : 1), net: Math.round((t.net + r.profit) * 100) / 100 }));
    return r;
  };

  const playerCls = state === "punching" ? "arc-punch" : state === "win" ? "arc-victory" : "arc-bob";
  const champCls = state === "win" ? "arc-ko" : state === "lose" ? "arc-block" : "arc-bob-d";

  return (
    <div data-testid="boxing-game" className="space-y-4">
      <HowToPlay id="boxing"
        steps={[
          "The ⚡ Signal Engine scores momentum on all 10 Volatility markets (recent move flow, price vs 20-tick average, fast/slow averages, streaks) and locks the strongest setup — or tells you to WAIT when everything is mixed.",
          "Read the setup card — it shows WHY (e.g. '7 of last 10 moves up · price above 20-tick average') and a 0–100 strength.",
          "Tap ⚡ PUNCH THE SIGNAL to take the detected side, or pick your own: UP-CUT bets the next tick HIGHER, BODY DROP bets LOWER.",
          "Your boxer throws the punch while the real 1-tick Rise/Fall contract settles (~2s).",
        ]}
        win="the next tick on the locked market closes in your direction — the champ hits the canvas (KO)."
        payout="~×1.9 your stake on a KO; a blocked punch loses the stake. Each round is close to 50/50."
        tip="decide a KO target before you start (e.g. +3 KOs net) and leave the ring while you're up." />

      <MarketScanner id="boxing" mode="rise" onChange={onScan}
        note="↑/↓ = share of up vs down moves in the last 50 ticks · recent flow, NOT a prediction" />

      {/* 🥊 the ring — casino fight night */}
      <div className={`relative rounded-xl border-2 border-[#F7B731]/50 bg-gradient-to-b from-[#10141f] to-[#0B0E14] overflow-hidden ${state === "lose" ? "ring-shake" : ""}`}
        style={{ boxShadow: "0 0 22px rgba(247,183,49,.1), inset 0 0 40px rgba(0,0,0,.55)" }}>
        <MarqueeRow pos="top" />
        <MarqueeRow pos="bottom" />
        <StageFX outcome={state === "win" ? "win" : state === "lose" ? "loss" : null} />
        <svg viewBox="0 0 320 140" className="w-full h-36">
          {/* crowd silhouettes */}
          <g opacity="0.35">
            {Array.from({ length: 16 }).map((_, i) => (
              <circle key={i} cx={10 + i * 20} cy={14 + (i % 3) * 5} r="5" fill="#1b2130" />
            ))}
          </g>
          {/* spotlight cone */}
          <polygon points="160,0 90,132 230,132" fill="#F7B731" className="spotlight" />
          {/* corner posts + ropes */}
          <rect x="6" y="28" width="6" height="96" rx="3" fill="#F23645" />
          <rect x="308" y="28" width="6" height="96" rx="3" fill="#2962FF" />
          <line x1="9" y1="38" x2="311" y2="38" stroke="#F23645" strokeWidth="3" opacity="0.8" />
          <line x1="9" y1="62" x2="311" y2="62" stroke="#ffffff" strokeWidth="2" opacity="0.3" />
          <line x1="9" y1="86" x2="311" y2="86" stroke="#2962FF" strokeWidth="3" opacity="0.8" />
          {/* canvas */}
          <rect x="0" y="118" width="320" height="22" fill="#20283c" />
          <rect x="0" y="118" width="320" height="4" fill="#2b3550" />
          {/* floor shadows */}
          <ellipse cx="110" cy="122" rx="26" ry="5" fill="#000" opacity="0.4" />
          <ellipse cx="210" cy="122" rx="26" ry="5" fill="#000" opacity="0.4" />
          <Boxer x={110} color="#2962FF" figCls={playerCls} />
          <Boxer x={210} color="#F23645" flip figCls={champCls} />
          {state === "win" && (
            <g>
              <path className="ko-star" d="M210 48 l4 10 10 1 -7 7 2 10 -9 -5 -9 5 2 -10 -7 -7 10 -1 z" fill="#F7B731" />
              <text x="186" y="52" fontSize="15" className="arc-float">⭐</text>
              <text x="228" y="46" fontSize="13" className="arc-float-d1">💫</text>
              <text x="244" y="58" fontSize="14" className="arc-float-d2">⭐</text>
            </g>
          )}
        </svg>
        {res && (state === "win" || state === "lose") && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 text-center arc-pop">
            <p className={`text-xl font-black font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
              {res.won ? "K.O.! 🏆" : "BLOCKED 🛡️"}
            </p>
            <p className={`text-xs font-mono ${res.won ? "text-[#089981]" : "text-[#F23645]"}`}>
              tick closed {res.won ? "your way" : "against you"} · {res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}
            </p>
          </div>
        )}
        {state === "punching" && (
          <p className="absolute top-2 left-1/2 -translate-x-1/2 text-[11px] font-mono text-[#F7B731] animate-pulse">settling the tick…</p>
        )}
      </div>

      <AutoRunBar id="boxing" auto={auto} noun="punches" />

      {mkt?.signal && !mkt.signal.wait ? (
        <button data-testid="boxing-signal-bet" disabled={busy || auto.active} onClick={() => {
          let last = mkt.signal.side === "RISE" ? "up" : "down";
          auto.burst(punch, () => { const s = mktRef.current?.signal; if (s && !s.wait) last = s.side === "RISE" ? "up" : "down"; return last; });
        }}
          className="w-full h-12 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          ⚡ Punch the signal — {mkt.signal.side} · {mkt.signal.strength}/100{auto.runs > 1 ? " · follows the best signal" : ""}
        </button>
      ) : (
        <div data-testid="boxing-signal-wait" className="w-full h-12 rounded-lg border border-dashed border-white/15 grid place-items-center text-[10px] font-mono text-white/45">
          ⏳ no clear setup right now — strong fighters wait for the opening
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="boxing-up" disabled={busy || auto.active} onClick={() => auto.burst(punch, "up")}
          className="h-16 rounded-lg bg-[#089981] hover:brightness-110 text-white transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
          <span className="font-black text-sm uppercase tracking-wide">🥊 Up-cut · Rise{mkt && mkt.risePct >= 56 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/75">KO if next tick closes higher{mkt ? ` · recent flow ↑${mkt.risePct}%` : ""}</span>
        </button>
        <button data-testid="boxing-down" disabled={busy || auto.active} onClick={() => auto.burst(punch, "down")}
          className="h-16 rounded-lg bg-[#F23645] hover:brightness-110 text-white transition disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
          <span className="font-black text-sm uppercase tracking-wide">🥊 Body drop · Fall{mkt && mkt.risePct <= 44 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/75">KO if next tick closes lower{mkt ? ` · recent flow ↓${100 - mkt.risePct}%` : ""}</span>
        </button>
      </div>

      <div className="flex items-center justify-center gap-3 text-[11px] font-mono text-white/60 flex-wrap">
        <span>KOs <b className="text-[#089981]">{tally.ko}</b> · Blocked <b className="text-[#F23645]">{tally.miss}</b> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b></span>
        {tally.ko >= 3 && tally.net > 0 && (
          <span data-testid="boxing-bank-tip" className="px-2 py-0.5 rounded-full border border-[#F7B731]/50 bg-[#F7B731]/10 text-[#F7B731]">🏆 3+ KOs up — champions leave the ring winners</span>
        )}
      </div>
      <p className="text-center text-[10px] font-mono text-white/40">each punch is a real 1-tick Rise/Fall contract (~×1.9) on the locked market · every round is ~50/50 · losses possible</p>
    </div>
  );
};

export default BoxingKO;
